import { useDispatch } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

import styled from 'styled-components';
import { colors, fontSizes, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { CLEAR_JOURNEY_CONVERSIONS_MODAL } from '../../../constants';
import { closeModal } from '../../../ducks';

export const CLEAR_EDITS_BUTTON_TEST_ID = 'clear-journey-conversions-button';

export interface ClearJourneyConversionsModalProps {
  /** callback to save changes */
  onSave: () => void;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Conversion event */
  conversionEvent: string;
}

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const StyledBoldText = styled.span`
  color: ${colors.textPrimary};
  font-size: ${fontSizes.s14};
  font-style: normal;
  font-weight: 700;
  line-height: ${pxToRem(20)};
`;

export const ClearJourneyConversionsModal: React.FC<
  React.PropsWithChildren<ClearJourneyConversionsModalProps>
> = ({ onSave, isOpen, conversionEvent }) => {
  const dispatch = useDispatch();

  const handleCloseModal = () => dispatch(closeModal(CLEAR_JOURNEY_CONVERSIONS_MODAL));

  const handleUpdate = () => {
    onSave();
    handleCloseModal();
  };

  return (
    <Modal
      id={CLEAR_JOURNEY_CONVERSIONS_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>Save changes?</ModalHeader>
      <StyledModalBody>
        <span>Are you sure you want to remove this journey&apos;s conversion event?</span>
        <span>
          This journey and its associated campaigns will stop recording conversions for the event{' '}
          <StyledBoldText>{conversionEvent}</StyledBoldText> , and any exit rules associated with
          this conversion event will be removed.
        </span>
      </StyledModalBody>
      <ModalFooter>
        <Button
          data-test={CLEAR_EDITS_BUTTON_TEST_ID}
          autoFocus
          onClick={handleUpdate}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          Save Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
};
